import { useChannelTypes, useDeleteChannelType, useUpdateChannelType } from '@/hooks/useChannelTypes';
import { Button } from '@/components/ui/button';
import { Edit, Trash2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useState } from 'react';
import type { VersionAwareProps } from '@/features/versions/types/props';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface ChannelTypeTableProps extends Pick<VersionAwareProps, 'providerId' | 'versionId'> {
  onEdit: (typeId: number) => void;
  isSequentialProvider?: boolean;
}

export function ChannelTypeTable({ 
  providerId, 
  versionId, 
  onEdit,
  isSequentialProvider 
}: ChannelTypeTableProps) {
  const { data: types, isLoading, error } = useChannelTypes(providerId, versionId);
  const deleteTypeMutation = useDeleteChannelType();
  const updateTypeMutation = useUpdateChannelType();
  const [typeToDelete, setTypeToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  if (error) {
    console.error('Error in ChannelTypeTable:', error);
    return (
      <div className="text-center py-8 text-red-500">
        Error loading channel types. Please try again.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center space-x-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-full" />
        </div>
        <div className="flex items-center space-x-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-full" />
        </div>
      </div>
    );
  }

  if (!types?.length) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground mb-4">
          {isSequentialProvider 
            ? 'No channel types found. Sequential provider types are created automatically.'
            : 'No channel types found. Create your first channel type which will be set as default.'}
        </p>
        
      </div>
    );
  }

  const handleDelete = async () => {
    if (!typeToDelete) return;
    
    try {
      setIsDeleting(true);
      
      const deletedType = types?.find(t => t.id === typeToDelete);
      const remainingTypes = types?.filter(t => t.id !== typeToDelete) || [];
      
      await deleteTypeMutation.mutateAsync({
        typeId: typeToDelete,
        providerId,
        versionId
      });

      // Reorder remaining types
      if (deletedType && remainingTypes.length > 0) {
        const updates = remainingTypes
          .filter(type => type.display_order > deletedType.display_order)
          .map(type => ({
            typeId: type.id,
            providerId,
            versionId,
            name: type.name,
            display_order: type.display_order - 1,
            is_default: type.is_default || false
          }));

        for (const update of updates) {
          await updateTypeMutation.mutateAsync(update);
        }
      }

      toast.success('Channel type deleted successfully');

      setTypeToDelete(null);
    } catch (error) {
      console.error('Delete operation failed:', error);
      toast.error(error instanceof Error
          ? error.message
          : 'Failed to delete channel type. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = (typeId: number) => {
    console.log('Triggering edit for type:', typeId);
    onEdit(typeId);
  };

  const handleDisplayOrderChange = async (typeId: number, newOrder: string) => {
    try {
      const currentType = types?.find(t => t.id === typeId);
      if (!currentType) return;

      // Get all types except current one
      const otherTypes = types?.filter(t => t.id !== typeId) || [];
      const newOrderNum = parseInt(newOrder);

      // Prepare updates for all affected types
      const updates = otherTypes.map(type => {
        let updatedOrder = type.display_order;
        if (type.display_order >= newOrderNum && type.display_order < currentType.display_order) {
          updatedOrder = type.display_order + 1;
        } else if (type.display_order <= newOrderNum && type.display_order > currentType.display_order) {
          updatedOrder = type.display_order - 1;
        }
        return {
          typeId: type.id,
          providerId,
          versionId,
          name: type.name,
          display_order: updatedOrder,
          is_default: type.is_default || false
        };
      });

      // Update current type
      await updateTypeMutation.mutateAsync({
        typeId,
        providerId,
        versionId,
        display_order: newOrderNum,
        name: currentType.name,
        is_default: currentType.is_default || false
      });

      // Update other types if needed
      for (const update of updates) {
        await updateTypeMutation.mutateAsync(update);
      }

      toast.success('Display order updated successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update display order');
    }
  };

  const getAvailableOrders = () => {
    // Always return sequential numbers from 1 to total number of types
    return Array.from({ length: types?.length || 0 }, (_, i) => i + 1);
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Display Order</TableHead>
            <TableHead>Default</TableHead>
            <TableHead className="w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {types?.map((type) => (
            <TableRow key={type.id}>
              <TableCell>{type.name}</TableCell>
              <TableCell>
                <Select
                  value={type.display_order.toString()}
                  onValueChange={(value) => handleDisplayOrderChange(type.id, value)}
                  disabled={isSequentialProvider}
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue>{type.display_order}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableOrders().map((order) => (
                      <SelectItem key={order} value={order.toString()}>
                        {order}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>{type.is_default ? 'Yes' : 'No'}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(type.id)}
                    disabled={isSequentialProvider}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setTypeToDelete(type.id)}
                    disabled={type.is_default || isDeleting || isSequentialProvider}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <AlertDialog 
        open={!!typeToDelete} 
        onOpenChange={(open) => !open && setTypeToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Channel Type</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this channel type? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
